import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { OccupationStep } from '../models/occupation.model';
import { formatHourLabel } from '../occupation-scales';

@Component({
  selector: 'app-occupation-tooltip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <div
      data-testid="occupation-tooltip"
      class="bg-elevated border-strong rounded-md border p-2 text-xs shadow-lg">
      <div class="border-weak mb-1 flex items-baseline justify-between gap-3 border-b pb-1">
        <span class="text-text-secondary font-mono text-[11px] tabular-nums">
          {{ range() }}
        </span>
        <span
          class="text-text-primary font-mono text-xs font-semibold tabular-nums"
          [class.text-danger]="isOver()">
          {{ step().used }}{{ capacity() === null ? '' : ' / ' + capacity() }}
        </span>
      </div>
      @if (step().holders.length) {
        <ul class="flex flex-col gap-0.5">
          @for (holder of step().holders; track holder.stopoverUid) {
            <li class="flex justify-between gap-3">
              <span class="text-text-secondary font-mono text-[11px]">
                {{ holder.routeUid }}
              </span>
              <span class="text-text-primary font-mono text-[11px] tabular-nums">
                {{ holder.quantity }}
              </span>
            </li>
          }
        </ul>
      } @else {
        <p class="text-text-tertiary text-[11px]">
          {{ 'visualization.occupation.tooltip.idle' | translate }}
        </p>
      }
    </div>
  `,
  // On the host, not the box inside it: the host has layout of its own and would otherwise
  // take the pointer off the mark it is describing. Since the tooltip is positioned over
  // that mark, the mark's mouseleave then fires, the tooltip unmounts, the cursor lands
  // back on the mark, and the pair flickers for as long as the cursor is still.
  styles: `
    :host {
      pointer-events: none;
    }
  `
})
export class OccupationTooltipComponent {
  readonly step = input.required<OccupationStep>();
  readonly capacity = input.required<number | null>();

  protected readonly isOver = computed(() => {
    const capacity = this.capacity();
    return capacity !== null && this.step().used > capacity;
  });

  protected readonly range = computed(
    () => `${formatHourLabel(this.step().fromMs)}–${formatHourLabel(this.step().toMs)}`
  );
}
